import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { fmtDate, kes } from "@/lib/money";
import { Page, Btn, Table, Tr, Td, Field, Loading } from "@/components/kit";

const input = "min-h-[40px] rounded-md border border-slate-300 px-2 text-sm text-slate-800";
const SOURCE = { opening: "Opening balance", manual: "Journal", station: "From operations", settlement: "Card settlement", statement: "From a statement" };

export default function Journals() {
  const { company } = useCompany();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    if (company) setRows((await api.get(`/companies/${company.id}/journals`, { params: { date_from: from || undefined, date_to: to || undefined } })).data);
  }, [company, from, to]);
  useEffect(() => { load(); }, [load]);
  const reverse = async (j) => {
    const reason = window.prompt(`Reverse "${j.memo}"? It stays on record with an equal and opposite journal. Reason:`);
    if (reason === null) return;
    try {
      await api.post(`/companies/${company.id}/journals/${j.id}/reverse`, { reason });
      toast.success("Reversed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Page title="Journals" actions={<Link to="/journals/new" className="inline-flex min-h-[40px] items-center rounded-md bg-brand px-4 text-sm font-semibold text-white hover:bg-brand/90">New journal</Link>}>
      <div className="mt-4 flex flex-wrap gap-3">
        <Field label="From"><input type="date" className={input} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" className={input} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>
      {!rows ? <Loading /> : (
        <Table minWidth={760} cols={[{ label: "Date" }, { label: "Ref" }, { label: "Details" }, { label: "Kind" }, { label: "Amount", align: "right" }, { label: "" }]}>
          {rows.map((j) => (
            <Fragment key={j.id}>
              <Tr className={j.reversed_by || j.reversal_of ? "text-slate-400" : ""}>
                <Td className="whitespace-nowrap">{fmtDate(j.date)}</Td>
                <Td className="text-slate-500">{j.ref}</Td>
                <Td><button type="button" className="text-left font-medium hover:text-brand hover:underline" aria-expanded={open === j.id} onClick={() => setOpen(open === j.id ? null : j.id)}>{open === j.id ? "▾" : "▸"} {j.memo}</button>
                  {j.reversed_by && <span className="ml-2 text-xs">reversed</span>}</Td>
                <Td className="text-slate-500">{SOURCE[j.source?.kind] || "Journal"}</Td>
                <Td right>{kes(j.total)}</Td>
                <Td>{!j.reversed_by && !j.reversal_of && j.source?.kind !== "station" && (
                  <button type="button" className="min-h-[34px] px-2 text-sm text-brand hover:underline" onClick={() => reverse(j)}>Reverse</button>)}</Td>
              </Tr>
              {open === j.id && (
                <tr><td colSpan={6} className="bg-[#F6F5F3] px-3 py-3">
                  <table className="w-full bg-white text-sm">
                    <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="px-2 py-1.5 text-left">Account</th><th className="px-2 py-1.5 text-left">Branch</th><th className="px-2 py-1.5 text-left">Line note</th><th className="px-2 py-1.5 text-right">Debit</th><th className="px-2 py-1.5 text-right">Credit</th></tr></thead>
                    <tbody>{j.lines.map((l, i) => (
                      <tr key={i} className="border-b border-slate-100"><td className="px-2 py-1.5"><Link to={`/ledger/${l.account_id}`} className="hover:text-brand hover:underline">{l.code} {l.account}</Link></td>
                        <td className="px-2 py-1.5 text-slate-500">{l.station || "Head office"}</td><td className="px-2 py-1.5 text-slate-500">{l.memo}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.debit ? kes(l.debit) : ""}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.credit ? kes(l.credit) : ""}</td></tr>
                    ))}</tbody>
                  </table>
                  <div className="mt-2 text-xs text-slate-500">Entered by {j.created_by} · {String(j.created_at).slice(0, 16).replace("T", " ")}</div>
                </td></tr>
              )}
            </Fragment>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="px-2 py-8 text-center text-slate-400">No journals</td></tr>}
        </Table>
      )}
    </Page>
  );
}
